import * as React from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { withTranslation } from "react-i18next";
import Spinner from "../common/Spinner";
import * as authSelectors from "../auth/authSelectors";
import MemberCardTypeListRow from "./MemberCardTypeListRow";
import { SortArrows } from "../common/SortArrows";
import * as _ from "lodash";
import MemberCardTypeListFilterForm from "./MemberCardTypeListFilterForm";
import * as naturalSort from "node-natural-sort";
import { StyledText } from "../../assets/typography/typography";
import { colors } from "../../components/colors";
import { Table } from "../../components/table-components/Table";
import { TableRowHeader } from "../../components/table-components/TableRowHeader";
import { TableCellHeaderText } from "../../components/table-components/TableCellHeaderText";
import { routes } from "../../appRoutingHelpers";
import { ButtonInternalLink } from "../../components/NewButtons";
import { useGetMembercardTypes } from "../../service/api";
import { useHandleFetchError } from "../../error-handler";

interface Props {}

const MemberCardTypeList: React.FC<Props> = (props) => {
  const t = (props as any).t;

  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );

  const {
    data: memberCardTypes,
    isLoading: isLoadingMemberCardTypes,
    error: memberCardTypesError,
  } = useGetMembercardTypes(organizationKeyName);

  const location = useLocation();

  const [sortColumn, setSortColumn] = React.useState("name");
  const [sortOrder, setSortOrder] = React.useState("ASC");
  const [filters, setFilters] = React.useState<any>({
    disabled: "no",
    visible: "yes",
  });

  const comparator = naturalSort({ caseSensitive: false });

  const memberCardTypesSorted = React.useMemo(() => {
    if (!memberCardTypes) {
      return [];
    }

    let result = memberCardTypes;

    if (sortColumn && sortColumn !== "") {
      result.sort((a, b) => comparator(a[sortColumn], b[sortColumn]));
    }

    if (sortOrder === "DESC") {
      result.reverse();
    }

    if (filters.name) {
      result = result.filter((x) => {
        return !!x.name?.toLowerCase().match(filters.name?.toLowerCase());
      });
    }

    if (filters.onlinePosGroup) {
      result = result.filter((x) => {
        return !!x.onlinePosGroup
          ?.toLowerCase()
          .match(filters.onlinePosGroup?.toLowerCase());
      });
    }

    const yesNo = ["yes", "no"];
    if (yesNo.includes(filters.showTripleUp)) {
      result = _.filter(result, [
        "showTripleUp",
        filters.showTripleUp === "yes" ? true : false,
      ]);
    }

    if (yesNo.includes(filters.visible)) {
      result = _.filter(result, [
        "visible",
        filters.visible === "yes" ? true : false,
      ]);
    }

    if (yesNo.includes(filters.disabled)) {
      result = _.filter(result, [
        "disabled",
        filters.disabled === "yes" ? true : false,
      ]);
    }

    return result;
  }, [comparator, memberCardTypes, sortColumn, sortOrder, filters]);

  const setColumnSortAndOrder = (col: string, ord: "ASC" | "DESC"): void => {
    setSortColumn(col);
    setSortOrder(ord);
  };

  useHandleFetchError(memberCardTypesError);

  return (
    <div className="container mt-7">
      <MemberCardTypeListFilterForm
        onSearch={setFilters}
        onReset={() => setFilters({})}
        defaultFilters={filters}
      />

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header border-bottom-0 mt-4 mb-4 justify-content-between">
              <StyledText
                color={colors.textPrimary}
                className="bodyHeaderOneSemi card-title"
              >
                Membercard Templates List
              </StyledText>
              <ButtonInternalLink to={location.pathname + "/new"}>
                Add new
              </ButtonInternalLink>
            </div>
            <div className="pl-5 pr-5">
              <Table>
                <thead>
                  <TableRowHeader>
                    <TableCellHeaderText className="pl-3">
                      {t("member_card_type_list.list.header.name")}
                      <SortArrows
                        col="name"
                        currentCol={sortColumn}
                        currentOrder={sortOrder}
                        setSortColumn={setColumnSortAndOrder}
                      />
                    </TableCellHeaderText>
                    <TableCellHeaderText>
                      {t("member_card_type_list.list.header.online_pos_group")}
                      <SortArrows
                        col="onlinePosGroup"
                        currentCol={sortColumn}
                        currentOrder={sortOrder}
                        setSortColumn={setColumnSortAndOrder}
                      />
                    </TableCellHeaderText>
                    <TableCellHeaderText>
                      {t("member_card_type_list.list.header.visible")}
                      <SortArrows
                        col="visible"
                        currentCol={sortColumn}
                        currentOrder={sortOrder}
                        setSortColumn={setColumnSortAndOrder}
                      />
                    </TableCellHeaderText>
                    <TableCellHeaderText>
                      {t("member_card_type_list.list.header.disabled")}
                      <SortArrows
                        col="disabled"
                        currentCol={sortColumn}
                        currentOrder={sortOrder}
                        setSortColumn={setColumnSortAndOrder}
                      />
                    </TableCellHeaderText>
                  </TableRowHeader>
                </thead>
                <tbody>
                  {memberCardTypesSorted.map((memberCardType) => (
                    <MemberCardTypeListRow
                      organization={organizationKeyName}
                      key={memberCardType.id}
                      memberCardType={memberCardType}
                      href={routes.memberCardTypeEdit(
                        organizationKeyName,
                        memberCardType.id
                      )}
                    />
                  ))}
                </tbody>
              </Table>
            </div>
            <div className="card-footer text-center mr-5 ml-5 px-0">
              {isLoadingMemberCardTypes && <Spinner />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withTranslation()(MemberCardTypeList);
